import {
    BuildCircleOutlined,
    Close,
    ErrorOutlined,
    InfoOutlined,
    Launch,
    MonitorHeart,
    NewReleasesOutlined,
} from '@mui/icons-material';
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineSeparator,
    timelineItemClasses,
} from '@mui/lab';
import { Alert, Box, Button, Drawer, IconButton, Stack, styled, Theme, Typography, useTheme } from '@mui/material';
import { orderBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnnouncements } from 'src/hooks/announcements/useAnnouncements';
import { useIsStatusPage } from 'src/hooks/useIsStatusPage';
import { AnnouncementTypeEnum } from 'src/infrastructure/rest-api/api-types';
import { mapAnnouncements } from 'src/mapping/announcements.mapping';

export const announcementColor = (announcementType: AnnouncementTypeEnum, theme: Theme) => {
    switch (announcementType) {
        case 'Information':
        case 'Maintenance':
            return theme.palette.info.main;
        case 'MinorIncident':
            return theme.palette.warning.dark;
        case 'MajorIncident':
            return theme.palette.error.main;
    }
};

export const StatusWidget: FC = () => {
    const theme = useTheme();
    const { data, markRead, cleanupRead } = useAnnouncements();
    const [isOpen, setIsOpen] = useState(false);
    const isStatusPage = useIsStatusPage();
    const navigate = useNavigate();

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const announcements = useMemo(() => mapAnnouncements(data), [data]);
    const filteredAnnouncements = useMemo(
        () =>
            orderBy(
                announcements.filter((announcement) => !announcement.resolved),
                ['created'],
                ['desc']
            ),
        [announcements]
    );

    const hasUnreadAnnouncements = useMemo(
        () => filteredAnnouncements.some((announcement) => !announcement.read),
        [filteredAnnouncements]
    );

    useEffect(() => {
        if (hasUnreadAnnouncements) {
            setIsOpen(true);
            timerRef.current = setTimeout(() => {
                setIsOpen(false);
            }, 5000);
        }
    }, [hasUnreadAnnouncements]);

    const statusColor = useMemo(() => {
        if (filteredAnnouncements.some((announcement) => announcement.type === 'MajorIncident')) {
            return theme.palette.error.main;
        }
        if (filteredAnnouncements.some((announcement) => announcement.type === 'MinorIncident')) {
            return theme.palette.warning.dark;
        }
        return theme.palette.success.main;
    }, [filteredAnnouncements, theme]);

    const toggleOpen = useCallback(() => {
        setIsOpen(!isOpen);

        // When opening the status widget, mark all unread announcements as read
        if (!isOpen) {
            markRead(
                filteredAnnouncements
                    .filter((announcement) => !announcement.read)
                    .map((announcement) => announcement.id)
            );
        }

        if (isOpen && timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, [isOpen, markRead, filteredAnnouncements]);

    useEffect(() => {
        cleanupRead();
    }, [cleanupRead]);
    return (
        <>
            <Button
                sx={{ color: statusColor }}
                variant="text"
                startIcon={<MonitorHeart fontSize="small" />}
                onClick={toggleOpen}
            >
                Status
            </Button>
            <Drawer
                open={isOpen && !isStatusPage}
                onClose={toggleOpen}
                anchor="right"
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 300,
                        boxSizing: 'border-box',
                    },
                }}
            >
                <Stack
                    sx={{
                        gap: 4,
                        px: 6,
                        py: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6">Status page</Typography>
                        <IconButton edge="end" onClick={toggleOpen}>
                            <Close />
                        </IconButton>
                    </Box>
                    <Button
                        onClick={() => {
                            navigate('/status');
                        }}
                        endIcon={<Launch />}
                        variant="contained"
                    >
                        Check status page
                    </Button>
                    <Stack>
                        {filteredAnnouncements.length > 0 ? (
                            <Timeline
                                sx={{
                                    [`& .${timelineItemClasses.root}:before`]: {
                                        flex: 0,
                                        padding: 0,
                                    },
                                    padding: 0,
                                    width: '100%',
                                }}
                            >
                                {filteredAnnouncements.map((announcement) => (
                                    <TimelineItem key={announcement.created?.toString()}>
                                        <TimelineSeparator>
                                            <TimelineItemDot announcementType={announcement.type}>
                                                {announcement.type === 'Information' && <InfoOutlined />}
                                                {announcement.type === 'Maintenance' && <BuildCircleOutlined />}
                                                {announcement.type === 'MajorIncident' && <NewReleasesOutlined />}
                                                {announcement.type === 'MinorIncident' && <ErrorOutlined />}
                                            </TimelineItemDot>
                                            <TimelineConnector sx={{ backgroundColor: '#D5D6DC' }} />
                                        </TimelineSeparator>
                                        <TimelineContent>
                                            <AnnouncementTitle variant="subtitle1" type={announcement.type}>
                                                {announcement.title}
                                            </AnnouncementTitle>
                                            <Typography
                                                variant="caption"
                                                color="primary"
                                                sx={{
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {(announcement.modified
                                                    ? announcement.modified
                                                    : announcement.created
                                                ).toLocaleTimeString([], {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </Typography>
                                            <Typography>{announcement.description}</Typography>
                                        </TimelineContent>
                                    </TimelineItem>
                                ))}
                            </Timeline>
                        ) : (
                            <Alert severity="success">System fully operational</Alert>
                        )}
                    </Stack>
                </Stack>
            </Drawer>
        </>
    );
};

const TimelineItemDot = styled(TimelineDot, {
    shouldForwardProp: (prop) => prop !== 'announcementType',
})<{ announcementType: AnnouncementTypeEnum }>(({ theme, announcementType }) => ({
    backgroundColor: announcementColor(announcementType, theme),
    color: 'white',
    boxShadow: 'none',
}));

const AnnouncementTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'type',
})<{ type?: AnnouncementTypeEnum }>(({ type, theme }) => ({
    whiteSpace: 'pre-wrap',
    color: announcementColor(type ?? 'Information', theme),
}));
